import { useEffect, useState } from "react";
import { usePrefixedNavigate } from "../hooks/usePrefixedNavigate";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../integrations/supabase/client";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Home,
  Building,
  FileText,
  MessageSquare,
  DollarSign,
  Award,
  Receipt,
  Heart,
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import { formatDate } from "../lib/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

interface DonorStats {
  totalDonations: number;
  hostelsSupported: number;
  donationLevel: string;
  donorName: string;
}

interface Donation {
  id: string;
  amount: number;
  donation_date: string;
  purpose: string | null;
  hostel_id: string | null;
  hostel: { name: string } | null;
}

interface FundingProject {
  id: string;
  name: string;
  city: string | null;
  funding_needed: number | null;
}

interface RawDonation {
  id: string;
  amount: number;
  donation_date: string | null;
  purpose: string | null;
  hostel_id: string | null;
}

export default function DonorDashboard() {
  const { user, userRole, loading } = useAuth();
  const navigate = usePrefixedNavigate();
  const [stats, setStats] = useState<DonorStats>({
    totalDonations: 0,
    hostelsSupported: 0,
    donationLevel: "bronze",
    donorName: "Donor",
  });
  const [recentDonations, setRecentDonations] = useState<Donation[]>([]);
  const [projects, setProjects] = useState<FundingProject[]>([]);

  useEffect(() => {
    if (!loading && userRole && userRole !== "donor") {
      navigate("/portal-selection");
    }
  }, [userRole, loading, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!user) {
        return;
      }

      try {
        // Fetch donor profile
        const { data: donorData } = await supabase
          .from("donors")
          .select("*")
          .eq("user_id", user.id)
          .maybeSingle();

        if (donorData) {
          // Fetch donations
          const { data: donations } = await supabase
            .from("donations")
            .select("id, amount, donation_date, purpose, hostel_id")
            .eq("donor_id", donorData.id)
            .order("donation_date", { ascending: false });

          const totalDonations =
            donations?.reduce(
              (sum: number, d: RawDonation) => sum + (d.amount ?? 0),
              0 as number
            ) ?? 0;
          const uniqueHostels = new Set(
            donations?.map((d: RawDonation) => d.hostel_id).filter(Boolean)
          );

          // Get hostel names for recent donations
          let donationsWithHostels: Donation[] = [];
          if (donations && donations.length > 0) {
            const hostelIds = donations
              .map((d: RawDonation) => d.hostel_id)
              .filter(Boolean);
            let hostelsMap: Record<string, { name: string }> = {};

            if (hostelIds.length > 0) {
              const { data: hostels } = await supabase
                .from("hostels")
                .select("id, name")
                .in("id", hostelIds as string[]);

              if (hostels) {
                hostelsMap = hostels.reduce(
                  (
                    acc: Record<string, { name: string }>,
                    h: { id: string; name: string }
                  ) => {
                    acc[h.id] = { name: h.name };
                    return acc;
                  },
                  {} as Record<string, { name: string }>
                );
              }
            }

            donationsWithHostels = (donations || [])
              .slice(0, 5)
              .map((d: RawDonation) => ({
                ...d,
                hostel: d.hostel_id ? hostelsMap[d.hostel_id] || null : null,
              })) as any;
          }

          setRecentDonations(donationsWithHostels);
          setStats({
            totalDonations,
            hostelsSupported: uniqueHostels.size,
            donationLevel: donorData.impact_level ?? "bronze",
            donorName: donorData.name ?? "Donor",
          });
        }

        // Fetch hostels needing funding (approved hostels with funding_needed > 0)
        const { data: fundingHostels } = await supabase
          .from("hostels")
          .select("id, name, city, funding_needed")
          .eq("status", "approved")
          .gt("funding_needed", 0)
          .limit(5);

        setProjects(fundingHostels ?? []);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [user]);

  const sidebarItems = [
    {
      label: "Dashboard",
      path: "/dashboard/donor",
      icon: <Home className="h-5 w-5" />,
    },
    {
      label: "Make Donation",
      path: "/donate",
      icon: <Heart className="h-5 w-5" />,
    },
    {
      label: "My Donations",
      path: "/donor/donations",
      icon: <DollarSign className="h-5 w-5" />,
    },
    {
      label: "Impact Reports",
      path: "/donor/impact-reports",
      icon: <FileText className="h-5 w-5" />,
    },
    {
      label: "Tax Receipts",
      path: "/donor/tax-receipts",
      icon: <Receipt className="h-5 w-5" />,
    },
    {
      label: "Help",
      path: "/donor/help",
      icon: <MessageSquare className="h-5 w-5" />,
    },
  ];

  const getLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case "gold":
        return "text-yellow-500";
      case "silver":
        return "text-gray-400";
      case "platinum":
        return "text-purple-500";
      default:
        return "text-amber-700";
    }
  };

  return (
    <DashboardLayout sidebarItems={sidebarItems} currentPath="/dashboard/donor">
      <div className="space-y-6">
        <div>
          <h2 className="mb-2 text-3xl font-bold text-foreground">
            Donor Dashboard
          </h2>
          <p className="text-muted-foreground">
            Thank you for your support, {stats.donorName}!
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-6 md:grid-cols-3">
          <Card className="border-primary/20 transition-shadow hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Donations
              </CardTitle>
              <DollarSign className="h-5 w-5 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">
                ₹{stats.totalDonations.toLocaleString()}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Lifetime contributions
              </p>
            </CardContent>
          </Card>

          <Card className="border-green-500/20 transition-shadow hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Hostels Supported
              </CardTitle>
              <Building className="h-5 w-5 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                {stats.hostelsSupported}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Hostels benefited
              </p>
            </CardContent>
          </Card>

          <Card className="border-amber-500/20 transition-shadow hover:shadow-md">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Impact Level
              </CardTitle>
              <Award
                className={`h-5 w-5 ${getLevelColor(stats.donationLevel)}`}
              />
            </CardHeader>
            <CardContent>
              <div
                className={`text-3xl font-bold uppercase ${getLevelColor(stats.donationLevel)}`}
              >
                {stats.donationLevel}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Recognition tier
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Projects to Support */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Projects Seeking Support</CardTitle>
            <p className="text-sm text-muted-foreground">
              Approved hostels looking for funding
            </p>
          </CardHeader>
          <CardContent>
            {projects.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                No projects currently seeking funding
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Hostel Name</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>Amount Needed</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projects.map(project => (
                    <TableRow key={project.id}>
                      <TableCell className="font-medium">
                        {project.name}
                      </TableCell>
                      <TableCell>{project.city ?? "N/A"}</TableCell>
                      <TableCell className="font-semibold">
                        ₹{(project.funding_needed ?? 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm">
                          <Heart className="mr-1 h-4 w-4" /> Donate
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Recent Donations */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Recent Donations</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/donor/donations")}
            >
              View All
            </Button>
          </CardHeader>
          <CardContent>
            {recentDonations.length === 0 ? (
              <div className="py-8 text-center">
                <p className="mb-4 text-muted-foreground">No donations yet</p>
                <Button>
                  <Heart className="mr-2 h-4 w-4" /> Make Your First Donation
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Hostel</TableHead>
                    <TableHead>Purpose</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recentDonations.map(donation => (
                    <TableRow key={donation.id}>
                      <TableCell className="font-medium">
                        {donation.hostel?.name ?? "General"}
                      </TableCell>
                      <TableCell>
                        {donation.purpose ?? "General Support"}
                      </TableCell>
                      <TableCell>
                        {formatDate(donation.donation_date)}
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        ₹{donation.amount.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button className="h-11 w-full">
                <Heart className="mr-2 h-4 w-4" /> Make a Donation
              </Button>
              <Button
                variant="outline"
                className="h-11 w-full"
                onClick={() => navigate("/donor/impact-reports")}
              >
                <FileText className="mr-2 h-4 w-4" /> View Impact Reports
              </Button>
              <Button
                variant="outline"
                className="h-11 w-full"
                onClick={() => navigate("/donor/tax-receipts")}
              >
                <Receipt className="mr-2 h-4 w-4" /> Download Tax Receipts
              </Button>
            </CardContent>
          </Card>

          <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5">
            <CardHeader>
              <CardTitle className="text-lg">Your Impact</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-4 text-sm leading-relaxed text-muted-foreground">
                Your contributions have helped{" "}
                <span className="font-semibold text-foreground">
                  {stats.hostelsSupported} hostels
                </span>{" "}
                provide better facilities to students across India.
              </p>
              <Button
                variant="default"
                className="h-11 w-full"
                onClick={() => navigate("/donor/impact-reports")}
              >
                View Detailed Impact
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
